import { useEffect, useState } from 'react';
import { listWorkspaces, type Workspace } from '../../api/workspaces';
import { getLearningContext, readLearner, readSubmission, learningPost, originalUrl, downloadEvidence, downloadExportOriginal,
  type ExportOriginal, type LearningContext, type Learner, type Submission, type Preview } from '../../api/learning';

export function EvidenceStatus({ loading, error, learner }: { loading: boolean; error: string; learner: Learner | null }) {
  if (loading) return <p role="status">Loading saved evidence…</p>;
  if (error) return <p role="alert" className="error">{error}</p>;
  if (!learner) return <p>No learner evidence is loaded. Load it or explicitly set up a new learner.</p>;
  if (!learner.submissions.length) return <p>No homework has been captured for this learner.</p>;
  return <p>{learner.submissions.length} saved submissions. Authority: {learner.workspace.authority === 'guardian' ? 'Guardian' : 'Student'}.</p>;
}

async function fileBase64(file: File): Promise<string> {
  if (file.size > 25 * 1024 * 1024) throw new Error('Each capture is limited to 25 MiB.');
  return new Promise((resolve, reject) => {
    const reader = new FileReader(); reader.onerror = () => reject(new Error('The file could not be read.'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] || ''); reader.readAsDataURL(file);
  });
}
async function digest(text: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function ParentEvidencePage() {
  const query = new URLSearchParams(window.location.search);
  const [runtime, setRuntime] = useState(query.get('runtime') || '');
  const [learnerId, setLearnerId] = useState(query.get('learner') || '');
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [context, setContext] = useState<LearningContext | null>(null);
  const [learner, setLearner] = useState<Learner | null>(null);
  const [selected, setSelected] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [ocr, setOcr] = useState('');
  const [page, setPage] = useState(1);
  const [skill, setSkill] = useState('');
  const [statement, setStatement] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [exportConfirmed, setExportConfirmed] = useState(false);
  const [exportOriginals, setExportOriginals] = useState<ExportOriginal[]>([]);
  const guardian = learner?.workspace.authority === 'guardian';
  const writable = context?.access_mode === 'full';

  useEffect(() => {
    let active = true;
    listWorkspaces().then((rows) => { if (active) setWorkspaces(rows); })
      .catch(() => { /* A handoff can still select its already-authorized runtime. */ });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    setLearner(null); setSelected(null); setPreview(null); setContext(null); setError(''); setNotice('');
    if (!runtime) return;
    let active = true; setLoading(true);
    getLearningContext(runtime).then((value) => { if (active) setContext(value); })
      .catch((failure) => { if (active) setError(failure.message || 'Workspace access could not be verified.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [runtime]);


  async function run(action: () => Promise<void>) {
    if (busy) return; setBusy(true); setError(''); setNotice('');
    try { await action(); } catch (failure) { setError(failure instanceof Error ? failure.message : 'This action did not complete.'); }
    finally { setBusy(false); }
  }
  async function reload() {
    if (!context || !learnerId) return;
    const value = await readLearner(context, learnerId); setLearner(value);
    if (selected) setSelected(await readSubmission(context, learnerId, selected.submission_id));
  }

  return <section className="learning-evidence-page">
    <h1>Learning — saved homework evidence</h1>
    <p>Use your signed-in Auteur account. A display mode or claimed role does not authorize guardian actions.</p>
    <p>Captured originals remain unchanged. OCR is unknown until a genuine guardian saves a correction.
      No automatic mastery, publishing or delivery is performed.</p>
    {workspaces.length > 0 && <label>Workspace <select aria-label="Workspace" value={runtime} disabled={busy}
      onChange={(event) => { setRuntime(event.target.value); setLearnerId(''); }}>
      <option value="">Choose your workspace</option>
      {workspaces.map((row) => <option key={row.workspace_id} value={row.workspace_id}>{row.ingress_host}</option>)}
    </select></label>}
    <details><summary>Saved workspace references</summary>
      <label>Workspace reference <input aria-label="Workspace reference" value={runtime} disabled={busy}
        onChange={(event) => setRuntime(event.target.value)} /></label>
      <label>Learner reference <input aria-label="Learner reference" value={learnerId} disabled={busy}
        onChange={(event) => { setLearnerId(event.target.value); setLearner(null); setSelected(null); setPreview(null); setNotice(''); }} /></label>
    </details>
    {context && !context.configured && <p role="alert">Evidence storage is not configured. Ask the operator to complete Learning setup.</p>}
    <button disabled={busy || !context?.configured || !learnerId} onClick={() => run(reload)}>Load saved evidence</button>
    <button disabled={busy || !context?.configured || !writable} onClick={() => run(async () => {
      const selectedId = learnerId || 'learner-' + crypto.randomUUID();
      await learningPost(context!, selectedId, '', {});
      const saved = await readLearner(context!, selectedId); setLearnerId(selectedId); setLearner(saved);
      const query = new URLSearchParams(window.location.search); query.set('runtime', runtime); query.set('learner', selectedId);
      window.history.replaceState(null, '', window.location.pathname + '?' + query.toString());
      setNotice('Learner evidence workspace saved.');
    })}>Set up new learner evidence</button>
    <EvidenceStatus loading={loading || busy} error={error} learner={learner} />
    {notice && <p role="status">{notice}</p>}
    {learner && <>
      <h2>Capture an original</h2>
      <input type="file" aria-label="Original homework file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf"
        disabled={busy || !writable} onChange={(event) => setFile(event.target.files?.[0] || null)} />
      <button disabled={busy || !writable || !file} onClick={() => run(async () => {
        const original = await fileBase64(file!);
        await learningPost(context!, learnerId, '/submissions', { pages: [{ page_number: 1, media_type: file!.type, original_base64: original }] });
        await reload(); setFile(null); setNotice('Original saved. OCR remains unknown until guardian correction.');
      })}>Save original</button>
      <h2>Saved submissions</h2>
      {learner.submissions.map((row) => <button key={row.submission_id} disabled={busy} onClick={() => run(async () => {
        setSelected(await readSubmission(context!, learnerId, row.submission_id)); setOcr(''); setConfirmed(false);
      })}>Saved submission · {row.page_count} pages · {row.confirmed_skill_count} confirmations</button>)}
      {selected && <article>
        <h2>Original and saved evidence</h2><p>Saved revision: {selected.revision}</p>
        {selected.pages.map((row) => <div key={row.page_number}>
          <a href={originalUrl(context!, learnerId, selected.submission_id, row.page_number)} download>Download original page {row.page_number}</a>
          <p>Original checksum: <code>{row.content_sha256}</code></p>
          <p>{row.low_confidence ? 'OCR unknown or uncertain' : 'Saved OCR text available'}</p>
          <p style={{ whiteSpace: 'pre-wrap' }}>{selected.ocr?.pages.find((item) => item.page_number === row.page_number)?.ocr_text || 'No extracted text.'}</p>
        </div>)}
        {(selected.confirmations || []).map((confirmation, index) => <details key={confirmation.skill_id + index}>
          <summary>Saved objective confirmation: {confirmation.skill_id}</summary>
          <p>Statement digest: <code>{confirmation.statement_sha256}</code></p>
          <p>Recorded confirmer: {confirmation.confirmed_by} · {confirmation.confirmed_at}</p>
          <p>The stored record contains the digest, not the statement text.</p>
        </details>)}
        {guardian && <>
          <label>Page <select aria-label="Correction page" value={page} disabled={busy} onChange={(event) => setPage(Number(event.target.value))}>
            {selected.pages.map((row) => <option key={row.page_number} value={row.page_number}>{row.page_number}</option>)}
          </select></label>
          <label>Guardian correction <textarea aria-label="Guardian correction" value={ocr} disabled={busy || !writable} onChange={(event) => setOcr(event.target.value)} /></label>
          <button disabled={busy || !writable || !ocr.trim()} onClick={() => run(async () => {
            await learningPost(context!, learnerId, '/submissions/' + encodeURIComponent(selected.submission_id) + '/pages/' + page + '/ocr', { text: ocr });
            await reload(); setNotice('Guardian correction saved.');
          })}>Save correction</button>
          <label>Objective code <input aria-label="Objective code" value={skill} disabled={busy || !writable} onChange={(event) => setSkill(event.target.value)} /></label>
          <label>Evidence statement <textarea aria-label="Evidence statement" value={statement} disabled={busy || !writable} onChange={(event) => { setStatement(event.target.value); setConfirmed(false); }} /></label>
          <label><input type="checkbox" checked={confirmed} disabled={busy || !writable} onChange={(event) => setConfirmed(event.target.checked)} />I reviewed the original and confirm this exact statement.</label>
          <button disabled={busy || !writable || !confirmed || !skill || !statement.trim()} onClick={() => run(async () => {
            const text = statement.trim();
            await learningPost(context!, learnerId, '/submissions/' + encodeURIComponent(selected.submission_id) + '/confirm',
              { skill_id: skill, statement: text, statement_sha256: await digest(text), actor_initiated: true });
            await reload(); setConfirmed(false); setNotice('Guardian confirmation saved.');
          })}>Confirm evidence statement</button>
        </>}
      </article>}
    </>}
    {context?.configured && learnerId && (guardian || !learner) && <section>
      <h2>Evidence export — JSON and originals</h2>
      <p>The summary contains saved metadata, OCR and confirmations; each original page downloads beside it with its checksum.
        A guardian can export even after a plan ends. Nothing is transmitted to an administrator.</p>
      <button disabled={busy} onClick={() => run(async () => {
        setPreview(await learningPost(context!, learnerId, '/export-preview', {}) as Preview); setExportConfirmed(false); setExportOriginals([]);
      })}>Preview export</button>
      {preview && <>
        <p>{preview.submission_count} submissions selected. Preview expires after {preview.expires_in_seconds} seconds.</p>
        <label><input type="checkbox" checked={exportConfirmed} disabled={busy} onChange={(event) => setExportConfirmed(event.target.checked)} />I confirm this scoped download.</label>
        <button disabled={busy || !exportConfirmed} onClick={() => run(async () => {
          const result = await downloadEvidence(context!, learnerId, preview);
          setExportOriginals(result.originals);
          setNotice('JSON download returned ' + result.bytes + ' bytes. Checksum: ' + result.sha256);
        })}>Download JSON summary</button>
        {exportConfirmed && exportOriginals.map((item) => <button key={item.submission_id + ':' + item.page_number} disabled={busy} onClick={() => run(async () => {
          const result = await downloadExportOriginal(context!, learnerId, preview, item);
          setNotice('Original page ' + item.page_number + ' returned ' + result.bytes + ' bytes. Checksum: ' + result.sha256);
        })}>Download exported original {item.submission_id} · page {item.page_number}</button>)}
      </>}
    </section>}
  </section>;
}
